import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import unescapedText from 'I18n/utils/unescapedText';
import getLang from 'I18n/utils/getLang';
/* @ts-expect-error module is untyped */

import CountrySelect from 'ui-addon-i18n/components/CountrySelect';
import UIFieldSet from 'UIComponents/form/UIFieldset';
import UIAccordionItem from 'UIComponents/accordion/UIAccordionItem';
import { SOLUTIONS_FILTERS_LANGUAGES } from 'marketplace-ui-common/constants/Languages';
import * as PartnerTiers from 'marketplace-ui-solutions-core/constants/PartnerTier';
import * as Budgets from 'marketplace-ui-solutions-core/constants/routes/BudgetRoute';
import * as Industries from 'marketplace-ui-common/constants/Industry';
import FilterToggleGroup from 'marketplace-ui-common/filters/components/filterToggleGroup/FilterToggleGroup';
/* @ts-expect-error module is untyped */

import LoadingWrapper from 'ecosystem-marketplace-core/components/LoadingWrapper';
import * as RequestState from 'ecosystem-marketplace-core/constants/RequestState';
import * as States from 'marketplace-ui-solutions-core/constants/routes/USStateRoute';
import { SOLUTIONS_EXCLUDED_COUNTRIES } from 'marketplace-ui-common/constants/Languages';
import { US } from 'marketplace-ui-solutions-core/constants/Country';
/* @ts-expect-error module is untyped */

import { OfficeLocationType } from '../constants/search/Filters';
/* @ts-expect-error module is untyped */

import BaseFilter from './filters/BaseFilter';
import FilterLoadingComponent from './filters/FilterLoadingComponent';
/* @ts-expect-error module is untyped */

import { getCredentialDisplayName, translateFilterValue } from '../utils/i18n';
import { ACCREDITATION, BUDGET, CERTIFICATION, LANGUAGE, TIER, SERVICE, INDUSTRY, COUNTRY, STATE, OFFICE_LOCATION, INTEGRATION } from '../constants/search/FilterKey';
/* @ts-expect-error module is untyped */

import { MULTI_SELECT_FILTER_KEYS } from '../constants/search/Filters';
import { getCertifications, getCertificationsRequestState
/* @ts-expect-error module is untyped */
} from '../selectors/certifications';
import { getAccreditations, getAccreditationsRequestState } from '../selectors/accreditations';
import { getServices, getServicesRequestState } from '../slices/serviceCatalogSlice';
import { getIntegrations, getIntegrationsRequestState } from '../slices/integrationsSlice';
const PartnerTierOrder = [PartnerTiers.ELITE, PartnerTiers.DIAMOND, PartnerTiers.PLATINUM, PartnerTiers.GOLD];

const sortPartnerTiers = (tierA, tierB) => PartnerTierOrder.indexOf(tierA.value || tierA) - PartnerTierOrder.indexOf(tierB.value || tierB);

const BudgetOrder = [Budgets.NONE, Budgets.LOWER, Budgets.MIDDLE, Budgets.UPPER];

const sortBudgets = (budgetA, budgetB) => BudgetOrder.indexOf(budgetA.value.toLowerCase()) - BudgetOrder.indexOf(budgetB.value.toLowerCase());

const StorefrontFilterAccordion = ({
  children,
  filterKey
}) => /*#__PURE__*/_jsx(UIAccordionItem, {
  className: "m-bottom-4",
  defaultOpen: true,
  flush: true,
  title: /*#__PURE__*/_jsx("strong", {
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: `ecosystem-marketplace-solutions-ui.storefront.filters.${filterKey}.label`
    })
  }),
  children: children
});

const StorefrontDesktopFilters = ({
  filters
}) => {
  const lang = getLang();
  const services = useSelector(getServices);
  const servicesRequestState = useSelector(getServicesRequestState);
  const certifications = useSelector(getCertifications);
  const certificationsRequestState = useSelector(getCertificationsRequestState);
  const accreditations = useSelector(getAccreditations);
  const accreditationRequestState = useSelector(getAccreditationsRequestState);
  const integrations = useSelector(getIntegrations);
  const integrationsRequestState = useSelector(getIntegrationsRequestState);
  const buildStaticFilterOptions = useCallback((filterKey, options) => {
    return Object.keys(options).map(option => ({
      text: translateFilterValue(filterKey, option),
      value: option
    }));
  }, []);
  const renderedServiceFilter = useMemo(() => {
    const serviceOptions = services.map(service => ({
      text: service.name,
      value: service.id
    }));
    return servicesRequestState === RequestState.SUCCEEDED ? /*#__PURE__*/_jsx(StorefrontFilterAccordion, {
      filterKey: SERVICE,
      children: /*#__PURE__*/_jsx(LoadingWrapper, {
        LoadingComponent: FilterLoadingComponent,
        ErrorComponent: Fragment,
        requestStatus: servicesRequestState,
        children: /*#__PURE__*/_jsx(BaseFilter, {
          collapsable: true,
          filterKey: SERVICE,
          name: SERVICE,
          multi: MULTI_SELECT_FILTER_KEYS.indexOf(SERVICE) !== -1,
          options: serviceOptions,
          value: filters[SERVICE],
          searchable: true,
          scrollable: true,
          SelectComponent: FilterToggleGroup,
          ariaLabel: unescapedText(`ecosystem-marketplace-solutions-ui.storefront.filters.${SERVICE}.label`)
        })
      })
    }) : null;
  }, [filters, services, servicesRequestState]);
  const renderedIndustryFilter = useMemo(() => {
    const industryOptions = buildStaticFilterOptions(INDUSTRY, Industries);
    return /*#__PURE__*/_jsx(StorefrontFilterAccordion, {
      filterKey: INDUSTRY,
      children: /*#__PURE__*/_jsx(BaseFilter, {
        collapsable: true,
        filterKey: INDUSTRY,
        name: INDUSTRY,
        multi: MULTI_SELECT_FILTER_KEYS.indexOf(INDUSTRY) !== -1,
        options: industryOptions,
        value: filters[INDUSTRY],
        searchable: true,
        scrollable: true,
        SelectComponent: FilterToggleGroup,
        ariaLabel: unescapedText(`ecosystem-marketplace-solutions-ui.storefront.filters.${INDUSTRY}.label`)
      })
    });
  }, [buildStaticFilterOptions, filters]);
  const renderedAccreditationsFilter = useMemo(() => {
    const accreditationsOptions = accreditations.map(accreditation => ({
      text: getCredentialDisplayName(accreditation, lang),
      value: accreditation.id.toString()
    }));
    return /*#__PURE__*/_jsx(StorefrontFilterAccordion, {
      filterKey: ACCREDITATION,
      children: /*#__PURE__*/_jsx(LoadingWrapper, {
        LoadingComponent: FilterLoadingComponent,
        ErrorComponent: Fragment,
        requestStatus: accreditationRequestState,
        children: /*#__PURE__*/_jsx(BaseFilter, {
          collapsable: true,
          filterKey: ACCREDITATION,
          name: ACCREDITATION,
          multi: MULTI_SELECT_FILTER_KEYS.indexOf(ACCREDITATION) !== -1,
          options: accreditationsOptions,
          value: filters[ACCREDITATION],
          scrollable: true,
          SelectComponent: FilterToggleGroup,
          ariaLabel: unescapedText(`ecosystem-marketplace-solutions-ui.storefront.filters.${ACCREDITATION}.label`)
        })
      })
    });
  }, [accreditations, accreditationRequestState, filters, lang]);
  const renderedOfficeLocationFilter = useMemo(() => {
    const officeLocationOptions = Object.keys(OfficeLocationType).map(locationType => ({
      text: translateFilterValue(OFFICE_LOCATION, OfficeLocationType[locationType]),
      value: OfficeLocationType[locationType]
    }));
    return /*#__PURE__*/_jsx("div", {
      className: "m-bottom-4",
      children: /*#__PURE__*/_jsx(BaseFilter, {
        filterKey: OFFICE_LOCATION,
        SelectComponent: FilterToggleGroup,
        options: officeLocationOptions,
        name: "office-location-basic-usage-radio",
        value: filters[OFFICE_LOCATION],
        ariaLabel: unescapedText(`ecosystem-marketplace-solutions-ui.storefront.filters.${OFFICE_LOCATION}.label`)
      })
    });
  }, [filters]);
  const renderedLocationFilter = useMemo(() => {
    const stateOptions = Object.keys(States).map(state => ({
      text: translateFilterValue(STATE, state),
      value: state
    }));
    return /*#__PURE__*/_jsx(StorefrontFilterAccordion, {
      filterKey: COUNTRY,
      children: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(BaseFilter, {
          collapsable: true,
          getFilterValue: evt => evt.target.value.toUpperCase(),
          filterKey: COUNTRY,
          multi: MULTI_SELECT_FILTER_KEYS.indexOf(COUNTRY) !== -1,
          value: filters[COUNTRY].toLowerCase(),
          SelectComponent: CountrySelect,
          optionsFilter: option => !SOLUTIONS_EXCLUDED_COUNTRIES.includes(option.value)
        }), filters[COUNTRY] === US && /*#__PURE__*/_jsx(BaseFilter, {
          filterKey: STATE,
          multi: MULTI_SELECT_FILTER_KEYS.indexOf(STATE) !== -1,
          options: stateOptions,
          value: filters[STATE]
        })]
      })
    });
  }, [filters]);
  const renderedLanguageFilter = useMemo(() => {
    const languageOptions = SOLUTIONS_FILTERS_LANGUAGES.map(language => ({
      text: translateFilterValue(LANGUAGE, language),
      value: language
    }));
    return /*#__PURE__*/_jsx(StorefrontFilterAccordion, {
      filterKey: LANGUAGE,
      children: /*#__PURE__*/_jsx(BaseFilter, {
        collapsable: true,
        filterKey: LANGUAGE,
        multi: MULTI_SELECT_FILTER_KEYS.indexOf(LANGUAGE) !== -1,
        options: languageOptions,
        value: filters[LANGUAGE]
      })
    });
  }, [filters]);
  const renderedBudgetFilter = useMemo(() => {
    const budgetOptions = buildStaticFilterOptions(BUDGET, Budgets);
    return /*#__PURE__*/_jsx(StorefrontFilterAccordion, {
      filterKey: BUDGET,
      children: /*#__PURE__*/_jsx(BaseFilter, {
        collapsable: true,
        filterKey: BUDGET,
        multi: MULTI_SELECT_FILTER_KEYS.indexOf(BUDGET) !== -1,
        name: BUDGET,
        options: budgetOptions,
        scrollable: true,
        SelectComponent: FilterToggleGroup,
        sortFn: sortBudgets,
        value: filters[BUDGET],
        ariaLabel: unescapedText(`ecosystem-marketplace-solutions-ui.storefront.filters.${BUDGET}.label`)
      })
    });
  }, [buildStaticFilterOptions, filters]);
  const renderedIntegrationsFilter = useMemo(() => {
    const integrationsOptions = integrations.map(integration => ({
      text: integration.displayName,
      value: integration.offeringId.toString()
    }));
    return /*#__PURE__*/_jsx(StorefrontFilterAccordion, {
      filterKey: INTEGRATION,
      children: /*#__PURE__*/_jsx(LoadingWrapper, {
        LoadingComponent: FilterLoadingComponent,
        ErrorComponent: Fragment,
        requestStatus: integrationsRequestState,
        children: /*#__PURE__*/_jsx(BaseFilter, {
          collapsable: true,
          filterKey: INTEGRATION,
          name: INTEGRATION,
          multi: true,
          options: integrationsOptions,
          value: filters[INTEGRATION],
          scrollable: true,
          searchable: true,
          SelectComponent: FilterToggleGroup,
          ariaLabel: unescapedText(`ecosystem-marketplace-solutions-ui.storefront.filters.${INTEGRATION}.label`),
          virtualized: true
        })
      })
    });
  }, [integrations, integrationsRequestState, filters]);
  const renderedCertificationsFilter = useMemo(() => {
    const certificationsOptions = certifications.map(cert => {
      const translatedTitle = getCredentialDisplayName(cert, lang);
      return {
        text: translatedTitle,
        value: cert.id.toString()
      };
    }).filter(opt => opt.text);
    return /*#__PURE__*/_jsx(LoadingWrapper, {
      LoadingComponent: FilterLoadingComponent,
      ErrorComponent: Fragment,
      requestStatus: certificationsRequestState,
      children: /*#__PURE__*/_jsx(StorefrontFilterAccordion, {
        filterKey: CERTIFICATION,
        children: /*#__PURE__*/_jsx(BaseFilter, {
          collapsable: true,
          filterKey: CERTIFICATION,
          multi: MULTI_SELECT_FILTER_KEYS.indexOf(CERTIFICATION) !== -1,
          options: certificationsOptions,
          value: filters[CERTIFICATION]
        })
      })
    });
  }, [certifications, certificationsRequestState, filters, lang]);
  const renderedPartnerTierFilter = useMemo(() => {
    const partnerTierOptions = Object.keys(PartnerTiers).filter(tier => tier !== PartnerTiers.NONE).map(tier => ({
      text: translateFilterValue(TIER, tier),
      value: tier
    }));
    return /*#__PURE__*/_jsx(StorefrontFilterAccordion, {
      filterKey: TIER,
      children: /*#__PURE__*/_jsx(BaseFilter, {
        collapsable: true,
        filterKey: TIER,
        multi: MULTI_SELECT_FILTER_KEYS.indexOf(TIER) !== -1,
        options: partnerTierOptions,
        sortFn: sortPartnerTiers,
        value: filters[TIER]
      })
    });
  }, [filters]);
  return (
    /*#__PURE__*/

    /* @ts-expect-error private property _size "does not exist on type" */
    _jsxs(UIFieldSet, {
      _size: "small",
      children: [renderedServiceFilter, renderedIndustryFilter, renderedAccreditationsFilter, renderedOfficeLocationFilter, renderedLocationFilter, renderedLanguageFilter, renderedBudgetFilter, renderedIntegrationsFilter, renderedCertificationsFilter, renderedPartnerTierFilter]
    })
  );
};

export default StorefrontDesktopFilters;